import React from 'react'
import classNames from 'classnames'
import { Label } from '@/ui/atoms/Label'
import { useTranslations } from 'next-intl'
import { Media } from '@/payload-types'
import { CMSImage } from '@/ui/atoms/CMSImage'
import { LabelClickable } from '@/ui/atoms/LabelClickable'
import { CMSButton } from '@/ui/components/CMSButton'
import { BackgroundGradient, GradientType } from '@/ui/atoms/BackgroundGradient'

export type CoreStage = {
  icon: string
  label: string
  href: string
}

export type ProcessCardProps = {
  title: string
  className?: string
  buttonText?: string
  buttonHref?: string | null
  coreStages?: CoreStage[]
  appearance?: 'default' | 'coming-soon'
  gradient?: GradientType
  backgroundImage?: Media | string | null
}

export const ProcessCard: React.FC<ProcessCardProps> = ({
  className: classNameFromProps,
  title,
  buttonText,
  buttonHref,
  coreStages = [],
  gradient,
  appearance = 'default',
  backgroundImage,
}) => {
  const t = useTranslations('public')
  const isComingSoon = appearance === 'coming-soon'

  return (
    <div
      className={classNames(
        '@container/process-card rounded-md overflow-hidden max-w-full w-full',
        isComingSoon
          ? 'bg-bg-subtle' // Light gray background for "coming soon"
          : 'relative md:aspect-16/9 min-h-[320px]', // For the background image and gradient
        classNameFromProps,
      )}
    >
      <div className={'w-full h-full @xl/process-card:p-12 p-6 flex flex-col '}>
        {/* Background with gradient overlay for default type */}
        {!isComingSoon && (
          <div className="absolute inset-0 overflow-hidden">
            {backgroundImage ? (
              <CMSImage
                fill
                resource={backgroundImage}
                className="absolute inset-0 w-full h-full object-cover brightness-95"
              />
            ) : (
              <BackgroundGradient
                type={gradient ?? GradientType.Gradient1}
                className={'absolute inset-0'}
              />
            )}
            <BackgroundGradient
              type={gradient ?? GradientType.Gradient1}
              className={'absolute inset-0'}
            />
          </div>
        )}

        {/* Container */}
        <div
          className={classNames(
            'relative z-10 flex mb-5 @xl/process-card:mb-2 items-baseline gap-4 ' +
              (!isComingSoon ? 'flex-col @xl/process-card:flex-row' : 'flex-row'),
          )}
        >
          <h3
            className={classNames(
              isComingSoon ? 'text-text-faded flex-1 min-w-0' : 'text-text-inverted',
              'text-headline-M-semibold flex-grow',
            )}
          >
            {title}
          </h3>

          {isComingSoon ? (
            <Label
              title={t('coming-soon')}
              variant={'neutral'}
              size={'medium'}
              className={'w-auto inline-flex'}
            />
          ) : (
            buttonText && (
              <CMSButton
                className={
                  'justify-between px-4 w-auto inline-flex bg-bg-base-transparent-10 backdrop-blur-sm rounded-sm canhover:hover:bg-bg-base-transparent-20 transition-all'
                }
                label={buttonText}
                appearance={'ghostInverted'}
                iconRight={'arrow-right-s-line'}
                showIconRight={true}
                linkType={'link'}
                link={{
                  type: 'reference',
                  url: buttonHref,
                }}
              />
            )
          )}
        </div>

        {!isComingSoon && coreStages && coreStages.length > 0 && (
          <div className={classNames('relative z-10 mt-auto')}>
            <span className="text-label-XS-regular text-text-faded-inverted mb-2 block">
              {t('core-stages')}
            </span>
            <div className={classNames('flex flex-wrap gap-2')}>
              {coreStages.map((stage, index) => (
                <LabelClickable
                  title={stage.label}
                  icon={stage.icon}
                  href={stage.href}
                  key={index}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
